import { IDynamicModel } from '../models';
import { resolveConflictResolution } from './dynamic-helpers';

/**
 * WI 34965: whether a text control (input, text or textarea) keeps its value when another control auto-pushes one
 * (`updateOnValueChange`). Opt-in through the target's `options.conflictResolution`, as for
 * typeaheads: absent or 'silent-default' always overwrites (unchanged behaviour). Any other mode
 * keeps a non-empty value that is not the last auto-filled one, i.e. one the user typed or a
 * saved record loaded. Text controls have no Keep/Switch popover, so 'prompt' also keeps.
 */
/** The free-text control types the keep rule applies to; every other type always overwrites. */
const DYNAMIC_TEXT_AUTO_FILL_TYPES: ReadonlyArray<IDynamicModel['type']> = [
  'input',
  'text',
  'textarea',
];

export function dynamicTextAutoFillKeeps(
  config: IDynamicModel,
  currentValue: unknown,
  lastAutoFilledValue: unknown
): boolean {
  if (
    !DYNAMIC_TEXT_AUTO_FILL_TYPES.includes(config.type) ||
    resolveConflictResolution(config).mode === 'silent-default'
  ) {
    return false;
  }
  const current = `${currentValue ?? ''}`.trim();
  return current !== '' && current !== `${lastAutoFilledValue ?? ''}`.trim();
}

/**
 * Task 35033: whether a clear pushed by another control (its lookup found no value) empties this
 * control. Only a value this control was auto-filled with is cleared, whatever its
 * conflictResolution, so a typed or saved value always survives.
 */
export function dynamicTextAutoFillClears(
  currentValue: unknown,
  lastAutoFilledValue: unknown
): boolean {
  const current = `${currentValue ?? ''}`.trim();
  return current !== '' && current === `${lastAutoFilledValue ?? ''}`.trim();
}
